import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';

import Icon from 'components/icon/Icon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { cn } from 'lib/utils';
import { ReadingData, ReadingDataWithSubParameters } from '../../../../types';

interface ScanVitalDetailProps {
  selectedVital: ReadingData | undefined;
  vital: ReadingDataWithSubParameters;
  onSelect: (key: string) => void;
}

export default function ScanVitalDetail({
  vital,
  selectedVital,
  onSelect,
}: ScanVitalDetailProps) {
  const { t } = useTranslation();

  const renderColorRange = (vital: ReadingData | undefined, name?: string) => {
    if (!vital) return null;

    return (
      <div
        className='relative grid items-end w-full'
        style={{
          gridTemplateColumns: `repeat(${vital?.colorRange?.length}, minmax(0, 1fr))`,
        }}
      >
        {vital?.colorRange?.map(({ color, range }, index: number) => (
          <div key={index} className='relative col-span-1'>
            <div
              style={{ backgroundColor: color }}
              className={cn('w-full h-2', range === vital.valueRange && 'h-4')}
            />
            {range === vital.valueRange && (
              <>
                <div
                  className={cn(
                    'absolute flex flex-col items-center -translate-x-1/2 left-1/2',
                    name ? '-top-7' : '-top-4'
                  )}
                >
                  {name ? (
                    <p className='-mb-1 text-sm leading-none'>{name}</p>
                  ) : null}
                  <Play
                    className='w-4 h-4 rotate-90'
                    style={{
                      color: vital.color,
                      fill: vital.color,
                    }}
                  />
                </div>
                <p
                  style={{ color }}
                  className='absolute text-sm font-semibold -translate-x-1/2 -bottom-5 left-1/2 text-nowrap'
                >
                  {range[0]} {range[1] ? `- ${range[1]}` : ''}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderIcon = (key: string, value: string | number, color: string) => (
    <Icon
      data-testid={`${key}-icon`}
      icon={key}
      size={28}
      color={value === 'Unknown' ? '#808080' : color}
    />
  );

  const renderCategory = (
    value: number | string,
    color: string,
    category: string
  ) => (
    <div
      className='col-span-2 text-sm font-semibold leading-tight text-center lg:col-span-1'
      style={{
        color: value === 'Unknown' ? '#808080' : color,
      }}
    >
      {category}
    </div>
  );

  const isBloodPressure = vital.key === 'BLOOD_PRESSURE';

  return (
    <div className='transition-colors duration-300 cursor-pointer border-y border-y-primary'>
      <div
        className={cn(
          'grid items-center justify-between w-full grid-cols-8 gap-y-4 py-4',
          selectedVital?.key === vital.key && 'bg-primary/10'
        )}
        onClick={() => onSelect(vital.key)}
        data-testid={`${vital.key}-vital`}
      >
        <div className='flex items-center col-span-6 gap-2 lg:col-span-3'>
          {renderIcon(vital.key, vital.value, vital.color)}

          <div className='font-medium leading-none'>
            <h2>{vital?.display || vital.name}</h2>
            {isBloodPressure ? (
              <p className='font-semibold'>
                {vital?.categories?.systolic?.value}/
                {vital?.categories?.diastolic?.value}{' '}
                {vital?.categories?.systolic?.unit}
              </p>
            ) : (
              <p className='font-semibold'>
                {vital.value} {vital?.unit}
              </p>
            )}
          </div>
        </div>

        {renderCategory(vital.value, vital.color, vital.category)}

        {isBloodPressure ? (
          <div className='flex flex-col items-center mt-4 mb-2 space-y-12 lg:items-end col-span-full lg:col-span-4'>
            {renderColorRange(vital?.categories?.systolic, 'systolic')}
            {renderColorRange(vital?.categories?.diastolic, 'diastolic')}
          </div>
        ) : (
          <div className='flex justify-center mb-2 lg:mb-0 lg:justify-end col-span-full lg:col-span-4'>
            {renderColorRange(vital)}
          </div>
        )}
      </div>

      {vital?.subParameters?.length ? (
        <div className='mt-2 mb-4'>
          <Accordion type='single' collapsible>
            <AccordionItem value='item-1'>
              <AccordionTrigger className='px-4 py-1 text-sm border rounded-sm text-primary border-primary'>
                {t('sub_parameters')}
              </AccordionTrigger>
              <AccordionContent>
                <div className='flex flex-col mt-4'>
                  {vital.subParameters.map((subparam) => (
                    <div
                      key={subparam.key}
                      className={cn(
                        'grid items-center justify-between w-full grid-cols-8 gap-y-4 py-3',
                        selectedVital?.key === subparam.key && 'bg-primary/10'
                      )}
                      onClick={() => onSelect(subparam.key)}
                    >
                      <div className='flex items-center col-span-6 gap-2 lg:col-span-3'>
                        {renderIcon(
                          subparam.key,
                          subparam.value,
                          subparam.color
                        )}

                        <div className='font-medium leading-none'>
                          <h2>{subparam.name}</h2>
                          <p className='font-semibold'>
                            {subparam.value} {subparam?.unit}
                          </p>
                        </div>
                      </div>

                      {renderCategory(
                        subparam.value,
                        subparam.color,
                        subparam.category
                      )}

                      <div className='flex justify-center mb-2 lg:mb-0 lg:justify-end col-span-full lg:col-span-4'>
                        {renderColorRange(subparam)}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      ) : null}
    </div>
  );
}
